import React, { useState, useEffect, useRef } from 'react';

import { API_URL } from "../config";


const formatPhoneNumber = (phone) => {
  // Remove any non-digit characters
  const cleaned = phone.replace(/\D/g, '');
  // Format as +X (XXX) XXX-XXXX
  const match = cleaned.match(/^(\d{1,2})(\d{3})(\d{3})(\d{4})$/);
  if (match) {
    return `+${match[1]} (${match[2]}) ${match[3]}-${match[4]}`;
  }
  return phone;
};

const formatMessageDate = (date) => {
  const messageDate = new Date(date);
  const now = new Date();
  const diffTime = Math.abs(now - messageDate);
  const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));

  if (diffDays === 0) {
    return messageDate.toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' });
  } else if (diffDays === 1) {
    return 'Ayer';
  } else if (diffDays < 7) {
    return messageDate.toLocaleDateString('es-ES', { weekday: 'long' });
  } else {
    return messageDate.toLocaleDateString('es-ES', { day: '2-digit', month: 'short' });
  }
};

const getLastMessagePreview = (message) => {
  if (message.length > 50) {
    return message.substring(0, 47) + '...';
  }
  return message;
};

const MessageStatus = ({ delivered, timestamp }) => {
  return (
    <div className="flex items-center space-x-1">
      {delivered ? (
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" style={{ transform: 'translateX(2px)' }} />
        </svg>
      ) : (
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
        </svg>
      )}
      <span className="text-xs text-gray-400">{formatMessageDate(timestamp)}</span>
    </div>
  );
};

const InteligenciaPanel = ({ selectedChat }) => {
  const [conversationMessages, setConversationMessages] = useState([]);
  const [insights, setInsights] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!selectedChat) {
      setConversationMessages([]);
      setInsights(null);
      setError(null);
      return;
    }
    setLoading(true);
    setError(null);
    fetch(`${API_URL}/api/whatsapp/messages/?phone_number=${selectedChat}`)
      .then(res => {
        if (!res.ok) throw new Error('No se pudieron obtener los mensajes');
        return res.json();
      })
      .then(data => {
        setConversationMessages(data);
        setLoading(false);
      })
      .catch(err => {
        setError(err.message);
        setLoading(false);
      });
  }, [selectedChat]);

  const handleAnalyze = async () => {
    setLoading(true);
    setError(null);
    setInsights(null);
    try {
      const messagesForLLM = conversationMessages.map(msg => ({
        role: msg.direction === 'outgoing' ? 'assistant' : 'user',
        content: msg.message,
      }));
      const response = await fetch(`${API_URL}/api/ai/process/`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ messages: messagesForLLM }),
      });
      const data = await response.json();
      setInsights(data);
    } catch (err) {
      setError('Error al obtener inteligencia');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="h-full p-4 bg-gray-50 border-l border-gray-200">
      <h2 className="text-lg font-semibold mb-4">Inteligencia</h2>
      {!selectedChat ? (
        <p className="text-gray-400">Selecciona una conversación para ver inteligencia.</p>
      ) : (
        <>
          <button
            onClick={handleAnalyze}
            disabled={loading || !conversationMessages.length}
            className="mb-4 px-4 py-2 bg-primary-500 text-white rounded hover:bg-primary-600 disabled:bg-gray-200 disabled:text-gray-400"
          >
            {loading ? 'Analizando...' : 'Obtener Inteligencia'}
          </button>
          {error && <div className="text-red-500 mb-2">{error}</div>}
          {insights && (
            <div className="space-y-2">
              {insights.response && (
                <div>
                  <span className="font-semibold">Respuesta de la IA:</span>
                  <div className="bg-white border rounded p-2 mt-1 text-gray-800">
                    {insights.response.length > 0
                      ? insights.response
                      : <span className='text-gray-400'>Sin respuesta</span>}
                  </div>
                </div>
              )}
              <pre className="whitespace-pre-wrap bg-white border rounded p-2 mt-1 font-mono text-sm">
                {JSON.stringify(insights, null, 2)}
              </pre>
            </div>
          )}
        </>
      )}
    </div>
  );
};

const RecentMessages = () => {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedChat, setSelectedChat] = useState(null);
  const [newMessage, setNewMessage] = useState('');
  const [sending, setSending] = useState(false);
  const messagesContainerRef = useRef(null);

  const scrollToBottom = () => {
    if (messagesContainerRef.current) {
      messagesContainerRef.current.scrollTop = messagesContainerRef.current.scrollHeight;
    }
  };

  // When a chat is selected, scroll to bottom and mark messages as read
  const handleChatSelect = async (phoneNumber) => {
    setSelectedChat(phoneNumber);
    // Use setTimeout to ensure the chat content is rendered before scrolling
    setTimeout(scrollToBottom, 0);

    // Mark all unread messages in this conversation as read
    try {
      const unreadMessages = conversations[phoneNumber]
        .filter(m => m.direction === 'incoming' && !m.read)
        .map(m => m.id);

      if (unreadMessages.length > 0) {
        await fetch(`${API_URL}/api/whatsapp/messages/mark-read/`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ message_ids: unreadMessages }),
        });

        // Update local state to reflect read status
        setMessages(prevMessages => 
          prevMessages.map(msg => 
            unreadMessages.includes(msg.id) 
              ? { ...msg, read: true } 
              : msg
          )
        );
      }
    } catch (error) {
      console.error('Error marking messages as read:', error);
    }
  };

  const handleSendMessage = async () => {
    if (!newMessage.trim() || !selectedChat) return;

    setSending(true);
    const messageToSend = newMessage.trim();
    setNewMessage('');

    // Create a temporary message object
    const tempMessage = {
      id: `temp-${Date.now()}`,
      message_sid: null,
      sender: 'whatsapp:your-number',
      recipient: selectedChat,
      message: messageToSend,
      direction: 'outgoing',
      created_at: new Date().toISOString(),
      delivered: false
    };

    // Add the temporary message immediately
    setMessages(prev => [...prev, tempMessage]);
    setTimeout(scrollToBottom, 0);

    try {
      const response = await fetch(`${API_URL}/api/whatsapp/send/`, {
        method: 'POST',
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          to: selectedChat,
          body: messageToSend
        })
      });

      if (!response.ok) {
        throw new Error('Failed to send message');
      }

      const result = await response.json();
      
      // Update the temporary message with the real one
      setMessages(prev => prev.map(msg => 
        msg.id === tempMessage.id 
          ? {
              ...msg,
              id: result.message_sid,
              message_sid: result.message_sid,
              delivered: true
            }
          : msg
      ));
    } catch (err) {
      console.error('Error sending message:', err);
      setError('Error al enviar el mensaje');
      // Remove the temporary message on error
      setMessages(prev => prev.filter(msg => msg.id !== tempMessage.id));
    } finally {
      setSending(false);
    }
  };

  useEffect(() => {
    const fetchMessages = async () => {
      try {
        const response = await fetch(`${API_URL}/api/whatsapp/messages/`, {
          method: 'GET',
          headers: {
            'Accept': 'application/json',
            'Content-Type': 'application/json',
          }
        });
        
        if (!response.ok) {
          throw new Error('Failed to fetch messages');
        }
        const data = await response.json();
        setMessages(data || []);
      } catch (err) {
        console.error('Error fetching messages:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchMessages();
    const interval = setInterval(fetchMessages, 30000);
    return () => clearInterval(interval);
  }, []);

  // Group messages by phone number (conversation)
  const conversations = messages.reduce((acc, message) => {
    // Normalize the phone number by removing any 'whatsapp:' prefix
    const phoneNumber = (message.direction === 'incoming' ? message.sender : message.recipient).replace('whatsapp:', '');
    
    // Initialize the array for this phone number if it doesn't exist
    if (!acc[phoneNumber]) {
      acc[phoneNumber] = [];
    }
    
    // Add message to the conversation
    acc[phoneNumber].push({
      ...message,
      // Normalize the sender and recipient fields
      sender: message.sender.replace('whatsapp:', ''),
      recipient: message.recipient?.replace('whatsapp:', '')
    });
    
    return acc;
  }, {});

  // Sort conversations by latest message
  const sortedConversations = Object.entries(conversations)
    .map(([phoneNumber, messages]) => ({
      phoneNumber,
      messages: messages.sort((a, b) => new Date(b.created_at) - new Date(a.created_at)),
      lastMessage: messages.reduce((latest, msg) => 
        new Date(msg.created_at) > new Date(latest.created_at) ? msg : latest
      ),
    }))
    .sort((a, b) => new Date(b.lastMessage.created_at) - new Date(a.lastMessage.created_at));

  if (loading) {
    return (
      <div className="flex justify-center items-center h-48">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded">
        {error}
      </div>
    );
  }

  return (
    <div className="flex h-full w-full">
      {/* Left: Conversations List */}
      <div className="w-1/4 border-r border-gray-200 overflow-y-auto">
        {/* Search bar - fixed at top */}
        <div className="flex-shrink-0 p-4 border-b border-gray-200">
          <div className="relative rounded-md shadow-sm">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <svg className="h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
            </div>
            <input
              type="text"
              placeholder="Buscar conversación..."
              className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
            />
          </div>
        </div>

        {/* Conversations - scrollable */}
        <div className="flex-1 overflow-y-auto">
          {sortedConversations.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-full text-gray-500 p-4">
              <svg className="w-16 h-16 mb-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z" />
              </svg>
              <p className="text-center text-sm font-medium">No hay conversaciones recientes</p>
              <p className="text-center text-xs text-gray-400 mt-1">Los mensajes nuevos aparecerán aquí</p>
            </div>
          ) : (
            <div className="divide-y divide-gray-200">
              {sortedConversations.map(({ phoneNumber, lastMessage, messages }) => {
                const unreadCount = messages.filter(m => m.direction === 'incoming' && !m.read).length;
                const hasUnread = unreadCount > 0;
                return (
                  <button
                    key={phoneNumber}
                    onClick={() => handleChatSelect(phoneNumber)}
                    className={`w-full text-left p-4 hover:bg-gray-100 transition-colors duration-150 focus:outline-none ${
                      selectedChat === phoneNumber ? 'bg-primary-50 hover:bg-primary-100' : ''
                    } ${hasUnread ? 'bg-blue-50' : ''}`}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex items-start space-x-4">
                        <div className={`flex-shrink-0 h-10 w-10 rounded-full flex items-center justify-center ${
                          hasUnread ? 'bg-primary-100' : 'bg-gray-100'
                        }`}>
                          <span className={`font-medium text-sm ${
                            hasUnread ? 'text-primary-700' : 'text-gray-600'
                          }`}>
                            {phoneNumber.slice(-2)}
                          </span>
                        </div>
                        <div>
                          <h3 className={`text-sm flex items-center ${
                            hasUnread ? 'font-semibold text-gray-900' : 'font-medium text-gray-700'
                          }`}>
                            {formatPhoneNumber(phoneNumber)}
                            {hasUnread && (
                              <span className="ml-2 bg-primary-500 text-white text-xs rounded-full h-5 w-5 flex items-center justify-center">
                                {unreadCount}
                              </span>
                            )}
                          </h3>
                          <p className={`text-sm mt-1 ${
                            hasUnread ? 'text-gray-900 font-medium' : 'text-gray-500'
                          }`}>
                            {lastMessage.direction === 'outgoing' && (
                              <span className="text-gray-400 mr-1">Tú:</span>
                            )}
                            {getLastMessagePreview(lastMessage.message)}
                          </p>
                        </div>
                      </div>
                      <div className="flex flex-col items-end">
                        <span className={`text-xs whitespace-nowrap ${
                          hasUnread ? 'text-primary-600 font-medium' : 'text-gray-400'
                        }`}>
                          {formatMessageDate(lastMessage.created_at)}
                        </span>
                        {lastMessage.direction === 'outgoing' && (
                          <span className="text-xs text-gray-400 mt-1">
                            {lastMessage.read ? '✓✓' : '✓'}
                          </span>
                        )}
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
          )}
        </div>
      </div>
      {/* Middle: Chat Panel */}
      <div className="w-2/4 flex flex-col">
        {selectedChat ? (
          <>
            {/* Chat header */}
            <div className="flex-shrink-0 p-4 border-b border-gray-200 bg-white">
              <div className="flex items-center">
                <div className="flex-shrink-0 h-10 w-10 rounded-full bg-gray-100 flex items-center justify-center">
                  <span className="font-medium text-sm text-gray-600">
                    {selectedChat.slice(-2)}
                  </span>
                </div>
                <div className="ml-4">
                  <h2 className="text-lg font-medium text-gray-900">
                    {formatPhoneNumber(selectedChat)}
                  </h2>
                </div>
              </div>
            </div>

            {/* Messages area */}
            <div 
              ref={messagesContainerRef}
              className="flex-1 overflow-y-auto p-4 space-y-4"
            >
              {conversations[selectedChat]
                ?.sort((a, b) => new Date(a.created_at) - new Date(b.created_at))
                .map((message, index) => (
                  <div
                    key={message.id}
                    className={`flex ${message.direction === 'outgoing' ? 'justify-end' : 'justify-start'}`}
                  >
                    <div
                      className={`max-w-[70%] px-4 py-2 mb-1
                        ${message.direction === 'outgoing'
                          ? 'bg-primary-500 text-white rounded-2xl rounded-br-md ml-auto'
                          : 'bg-white text-gray-900 border border-gray-200 rounded-2xl rounded-bl-md mr-auto'}
                      `}
                      style={{
                        boxShadow: message.direction === 'outgoing'
                          ? '0 2px 8px rgba(80, 80, 200, 0.08)'
                          : '0 2px 8px rgba(120, 120, 120, 0.06)'
                      }}
                    >
                      <p className="text-sm">{message.message}</p>
                      <div className={`mt-1 ${
                        message.direction === 'outgoing' ? 'text-primary-100' : 'text-gray-500'
                      }`}>
                        {message.direction === 'outgoing' ? (
                          <MessageStatus 
                            delivered={message.delivered} 
                            timestamp={message.created_at}
                          />
                        ) : (
                          <span className="text-xs">{formatMessageDate(message.created_at)}</span>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
            </div>

            {/* Message input */}
            <div className="flex-shrink-0 p-4 border-t border-gray-200 bg-white">
              <div className="flex space-x-4">
                <input
                  type="text"
                  value={newMessage}
                  onChange={(e) => setNewMessage(e.target.value)}
                  onKeyPress={(e) => {
                    if (e.key === 'Enter' && !e.shiftKey) {
                      e.preventDefault();
                      handleSendMessage();
                    }
                  }}
                  placeholder="Escribe un mensaje..."
                  className="flex-1 rounded-lg border border-gray-300 px-4 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                />
                <button
                  onClick={handleSendMessage}
                  disabled={!newMessage.trim() || sending}
                  className={`px-4 py-2 rounded-lg font-medium ${
                    !newMessage.trim() || sending
                      ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                      : 'bg-primary-500 text-white hover:bg-primary-600'
                  }`}
                >
                  {sending ? 'Enviando...' : 'Enviar'}
                </button>
              </div>
            </div>
          </>
        ) : (
          <div className="flex-1 flex items-center justify-center bg-gray-50">
            <div className="text-center">
              <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
              </svg>
              <h3 className="mt-2 text-sm font-medium text-gray-900">No hay chat seleccionado</h3>
              <p className="mt-1 text-sm text-gray-500">Selecciona una conversación para comenzar a chatear</p>
            </div>
          </div>
        )}
      </div>
      {/* Right: Inteligencia Panel */}
      <div className="w-1/4 border-l border-gray-200 overflow-y-auto">
        <InteligenciaPanel selectedChat={selectedChat} />
      </div>
    </div>
  );
};

export default RecentMessages; 